import { Injectable } from '@angular/core'
import { ModalController } from '@ionic/angular'
import { Article } from '../models/article'
import { Stock } from '../models/stock'
import { Translation } from './translation.service'
import { StockRepository } from '../repositories/stock.repository'
import { mapStockToCreateStockCommandParams } from './dao/stock.dao'
import { ArticleRepository } from '../repositories/article.repository'
import {
  DynamicInput,
  DynamicFormComponent,
} from '../shared/components/dynamic-form/dynamic-form.component'
import { AbstractControl, ValidationErrors } from '@angular/forms'
import { ModalComponent, ModalDismissAction, ModalStyle } from '@doka-shared/common'

class StockImportError extends Error {}

@Injectable({
  providedIn: 'root',
})
export class StockService {
  constructor(
    private readonly modalCtrl: ModalController,
    private readonly translate: Translation,
    private readonly stockRepository: StockRepository,
    private readonly articleRepository: ArticleRepository
  ) {}

  public async importStockListFromFile(
    text: string,
    fileName: string,
    existingStock?: Stock
  ): Promise<Stock | null> {
    const stockName = fileName.substring(fileName.lastIndexOf('/') + 1, fileName.lastIndexOf('.'))

    const importedArticles = await this.importArticlesFromFile(text, fileName)

    if (importedArticles.length < 1) {
      return null
    } else if (existingStock) {
      const newStockArticles = existingStock.articles ?? []

      for (const article of importedArticles) {
        article.stockId = existingStock.id
        const existingArticle = newStockArticles.find((a) => a.articleId === article.articleId)
        try {
          if (existingArticle) {
            existingArticle.amount += article.amount
            await this.articleRepository.update({
              ...existingArticle,
            })
          } else {
            await this.articleRepository.create(article)
            newStockArticles.push(article)
          }
        } catch {
          return null
        }
      }

      return {
        id: existingStock.id,
        name: existingStock.name,
        date: existingStock.date,
        articles: newStockArticles,
      }
    } else {
      return this.createStock(stockName, importedArticles)
    }
  }

  public async createStock(stockName?: string, articleList?: Article[]): Promise<Stock | null> {
    const modal = await this.updateOrCreateStockModal(
      stockName ?? this.translate.translate('STOCK.IMPORT_DEFAULT_NAME')
    )
    await modal.present()
    const { data, role } = await modal.onDidDismiss()

    if (role === 'cancel') {
      return null
    } else if (role === ModalDismissAction.CONFIRM && data) {
      const stockList: Stock = {
        id: 0,
        name: data,
        date: new Date(),
        articles: articleList,
      }

      try {
        return await this.stockRepository.createStockWithArticles(
          mapStockToCreateStockCommandParams(stockList)
        )
      } catch {
        return null
      }
    }

    return null
  }

  public async updateOrCreateStockModal(
    value: string,
    currentStock?: Stock
  ): Promise<HTMLIonModalElement> {
    const inputs: DynamicInput[] = [
      {
        type: 'text',
        value,
        placeholder: value,
      },
    ]

    const validationFn = (control: AbstractControl): ValidationErrors => {
      const errors: ValidationErrors = {}
      if (!control.value) errors.emptyInput = this.translate.translate('STOCK.IMPORT_SET_NAME')
      return errors
    }

    return await this.modalCtrl.create({
      component: ModalComponent,
      backdropDismiss: false,
      cssClass: 'small-modal',
      componentProps: {
        title: currentStock
          ? this.translate.translate('GENERAL.RENAME')
          : this.translate.translate('STOCK.IMPORT_SET_NAME'),
        cancelButtonLabel: this.translate.translate('GENERAL.CANCEL'),
        confirmButtonLabel: this.translate.translate('GENERAL.OK'),
        dynamicComponent: DynamicFormComponent,
        dynamicComponentProps: { options: inputs, validationFn },
      },
    })
  }

  private async importArticlesFromFile(text: string, fileName: string): Promise<Article[]> {
    let articles: Article[] = []
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1)
    if (extension === 'ma7') {
      try {
        articles = await this.importArticlesFromMa7StockFile(text)
      } catch (_: unknown) {
        await this.showStockImportErrorAlert(
          undefined,
          this.translate.translate('STOCK.IMPORT_ERROR_MA7')
        )
      }
    } else if (extension === 'csv') {
      try {
        articles = await this.importArticlesFromCsvStockFile(text)
      } catch (error: unknown) {
        const stockErrorMessage = error instanceof StockImportError ? error.message : undefined
        if (stockErrorMessage) {
          await this.showStockImportErrorAlert(
            this.translate.translate('STOCK.IMPORT_ERROR'),
            stockErrorMessage
          )
        } else {
          await this.showStockImportErrorAlert(
            undefined,
            this.translate.translate('STOCK.IMPORT_ERROR_CSV')
          )
        }
      }
    } else {
      // wrong format
      await this.showStockImportErrorAlert(
        undefined,
        this.translate.translate('STOCK.IMPORT_WRONG_FORMAT')
      )
    }

    const duplicateArticleId = this.checkForDuplicates(articles)
    if (duplicateArticleId) {
      articles = []
      await this.showStockImportErrorAlert(
        undefined,
        this.translate
          .translate('STOCK.IMPORT_DUPLICATE_ERROR')
          .replace('{{articleId}}', duplicateArticleId)
      )
    }

    return articles
  }

  private async importArticlesFromMa7StockFile(text: string): Promise<Article[]> {
    const domParser = new DOMParser()
    const xmlDocument = domParser.parseFromString(text, 'text/xml')
    const xmlArticles: HTMLCollection = xmlDocument.getElementsByTagName('ListLine')
    const articles: Article[] = []

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < xmlArticles.length; i++) {
      const amount: number = +(xmlArticles[i].getElementsByTagName('Quantity')[0].textContent ?? 0)
      const articleName: string | null =
        xmlArticles[i].getElementsByTagName('Designation')[0].textContent
      const id: string | null = xmlArticles[i].getElementsByTagName('ArticleNumber')[0].textContent

      if (articleName && id) {
        const article: Article = {
          id: 0,
          amount,
          articleId: id,
          name: articleName,
          stockId: 0,
        }

        articles.push(article)
      }
    }

    return articles
  }

  private async importArticlesFromCsvStockFile(text: string): Promise<Article[]> {
    const strDelimiter = ';'
    const objPattern = new RegExp(
      '(\\' +
        strDelimiter +
        '|\\r?\\n|\\r|^)' +
        '(?:"([^"]*(?:""[^"]*)*)"|' +
        '([^"\\' +
        strDelimiter +
        '\\r\\n]*))',
      'gi'
    )

    const articles: Article[] = []
    const parsedCsvData: string[][] = [[]]
    let arrMatches: RegExpExecArray | null = null

    while ((arrMatches = objPattern.exec(text))) {
      const strMatchedDelimiter = arrMatches[1]
      if (strMatchedDelimiter.length && strMatchedDelimiter !== strDelimiter) {
        parsedCsvData.push([])
      }

      let strMatchedValue
      if (arrMatches[2]) {
        strMatchedValue = arrMatches[2].replace(new RegExp('""', 'g'), '"')
      } else {
        strMatchedValue = arrMatches[3]
      }
      parsedCsvData[parsedCsvData.length - 1].push(strMatchedValue)
    }

    for (let i = 1; i < parsedCsvData.length; i++) {
      // sometimes csv [from numbers - Mac] adds an empty row
      const row = parsedCsvData[i]
      if (row[0].length > 0) {
        const id = row[0]
        const amount: number = +row[1]
        const name: string = row[2]

        if (id == null || id.length < 8 || id.length > 9) {
          throw this.createCsvImportError(i + 1, this.translate.translate('GENERAL.ARTICLE_NUMBER'))
        }

        if (isNaN(amount) || amount < 0 || amount > 9999) {
          throw this.createCsvImportError(i + 1, this.translate.translate('GENERAL.QUANTITY'))
        }

        if (name.length < 1) {
          throw this.createCsvImportError(i + 1, this.translate.translate('GENERAL.ARTICLE_NAME'))
        }

        if (amount !== undefined && name && id) {
          const article: Article = {
            id: 0,
            amount,
            articleId: id,
            name,
            stockId: 0,
          }

          articles.push(article)
        }
      }
    }
    return articles
  }

  private createCsvImportError(rowNumber: number, columnName: string): Error {
    const columnLabel = this.translate.translate('STOCK.IMPORT_COLUMN')
    const rowNumberLabel = this.translate.translate('STOCK.IMPORT_ROW')

    return new StockImportError(`
${columnLabel}: <b>${columnName}</b></br>
${rowNumberLabel}: <b>${rowNumber}</b>`)
  }

  private async showStockImportErrorAlert(
    subHeader: string | undefined,
    message: string | undefined
  ): Promise<void> {
    const innerHTML = '<p>' + subHeader + '</p>' + '<p>' + message + '</p>'
    const modal = await this.modalCtrl.create({
      component: ModalComponent,
      cssClass: 'small-modal',
      componentProps: {
        modalStyle: ModalStyle.Alert,
        title: this.translate.translate('GENERAL.ERROR'),
        innerHTML,
        cancelButtonLabel: this.translate.translate('GENERAL.CLOSE'),
      },
    })
    await modal.present()
  }

  async createDeleteArticleAlert(article: Article): Promise<boolean> {
    const modal = await this.modalCtrl.create({
      component: ModalComponent,
      backdropDismiss: false,
      cssClass: 'small-modal',
      componentProps: {
        title: this.translate.translate('STOCK.ARTICLE.DELETE_HEADER', {
          articleName: article.name,
        }),
        text: this.translate.translate('STOCK.ARTICLE.DELETE_MESSAGE'),
        cancelButtonLabel: this.translate.translate('GENERAL.CANCEL'),
        criticalActionButtonLabel: this.translate.translate('GENERAL.DELETE'),
        modalStyle: ModalStyle.Alert,
        icon: 'error',
      },
    })

    await modal.present()
    const { role } = await modal.onDidDismiss()
    if (role === ModalDismissAction.CRITICAL_ACTION) {
      await this.articleRepository.delete(article)
      return true
    }
    return false
  }

  private checkForDuplicates(articles: Article[]): string | undefined {
    const foundArticleIds = new Map()
    for (const article of articles) {
      if (foundArticleIds.has(article.articleId)) {
        return article.articleId
      }
      foundArticleIds.set(article.articleId, '')
    }

    return
  }
}
